import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateMe } from "../lib/api";
import { useAuth } from "../lib/useAuth";

const COHORT_OPTIONS = {
  cohort_residency: [
    { value: "resident", label: "Resident in Lebanon" },
    { value: "diaspora", label: "Diaspora" },
  ],
  cohort_age_bracket: [
    { value: "18-25", label: "18–25" },
    { value: "26-40", label: "26–40" },
    { value: "41-60", label: "41–60" },
    { value: "60+", label: "60+" },
  ],
  cohort_region_lebanon: [
    { value: "beirut", label: "Beirut" },
    { value: "mount_lebanon", label: "Mount Lebanon" },
    { value: "north", label: "North" },
    { value: "south", label: "South" },
    { value: "beqaa", label: "Beqaa" },
    { value: "akkar", label: "Akkar" },
    { value: "not_applicable", label: "Not applicable" },
  ],
  cohort_region_diaspora: [
    { value: "americas", label: "Americas" },
    { value: "europe", label: "Europe" },
    { value: "gcc", label: "GCC" },
    { value: "africa", label: "Africa" },
    { value: "asia_oceania", label: "Asia/Oceania" },
    { value: "not_applicable", label: "Not applicable" },
  ],
  cohort_sect: [
    { value: "maronite", label: "Maronite" },
    { value: "sunni", label: "Sunni" },
    { value: "shia", label: "Shia" },
    { value: "druze", label: "Druze" },
    { value: "greek_orthodox", label: "Greek Orthodox" },
    { value: "greek_catholic", label: "Greek Catholic" },
    { value: "armenian", label: "Armenian" },
    { value: "other_christian", label: "Other Christian" },
    { value: "other", label: "Other" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
  ],
} as const;

type CohortKey = keyof typeof COHORT_OPTIONS;

export default function Profile() {
  const { user, loading, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<Partial<Record<CohortKey, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/register", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      setForm({
        cohort_residency: user.cohort_residency ?? "",
        cohort_age_bracket: user.cohort_age_bracket ?? "",
        cohort_region_lebanon: user.cohort_region_lebanon ?? "",
        cohort_region_diaspora: user.cohort_region_diaspora ?? "",
        cohort_sect: user.cohort_sect ?? "",
      });
    }
  }, [user]);

  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      const patch: Record<string, string | null> = {};
      for (const [k, v] of Object.entries(form)) {
        patch[k] = v === "" ? null : v ?? null;
      }
      await updateMe(patch);
      await refresh();
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  if (loading || !user) return <p className="text-muted">Loading…</p>;

  return (
    <div className="max-w-prose space-y-8">
      <header>
        <h1 className="text-3xl font-semibold">Profile</h1>
        <p className="mt-1 text-muted">
          Signed in as <strong>@{user.username}</strong>.
        </p>
      </header>

      <section>
        <h2 className="text-xl font-semibold">Cohort</h2>
        <p className="mt-1 text-sm text-muted">
          These fields are aggregated for cohort-filtered results, with a privacy
          threshold of 10 votes per bucket. They are never tied to your votes in any
          public response. All are optional.
        </p>
        <div className="mt-4 space-y-4">
          {(Object.keys(COHORT_OPTIONS) as CohortKey[]).map((key) => (
            <Field
              key={key}
              label={fieldLabel(key)}
              value={form[key] ?? ""}
              onChange={(v) => setForm({ ...form, [key]: v })}
              options={COHORT_OPTIONS[key].slice() as Array<{ value: string; label: string }>}
            />
          ))}
        </div>
        <div className="mt-6 flex items-center gap-3">
          <button onClick={save} disabled={saving} className="btn-primary">
            {saving ? "Saving…" : "Save"}
          </button>
          {saved && <span className="text-sm text-cedar">Saved.</span>}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold">Account</h2>
        <button
          onClick={async () => {
            await logout();
            navigate("/", { replace: true });
          }}
          className="btn-ghost mt-3"
        >
          Sign out
        </button>
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input">
        <option value="">(not set)</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function fieldLabel(k: CohortKey): string {
  switch (k) {
    case "cohort_residency":
      return "Residency";
    case "cohort_age_bracket":
      return "Age";
    case "cohort_region_lebanon":
      return "Region (in Lebanon)";
    case "cohort_region_diaspora":
      return "Region (diaspora)";
    case "cohort_sect":
      return "Sect (optional, anonymous)";
  }
}

